import { ApiError } from 'src/errors/apiError';
import {
  CreateApprovalRequest,
  CreateApprovalResponse,
  RetrieveApprovalResponse,
  ApprovalStatus,
} from 'src/types';

export class ApprovalService {
  private static readonly checkApprovalApprovalStatusInterval = 1500;
  private static readonly checkApprovalApprovalStatusTimeout = 8 * 60 * 1000;
  static readonly APPROVAL_API_BASE_URL: string =
    'https://demo.uat.idp.cifas.org.uk';

  async createApproval(
    userId: string,
    product: string,
    supplier: string,
    memberNumber: number,
    accessToken: string
  ): Promise<CreateApprovalResponse> {
    const requestBody: CreateApprovalRequest = {
      UserId: userId,
      Product: product,
      Supplier: supplier,
      MemberNumber: memberNumber,
    };

    try {
      const response = await fetch(
        `${ApprovalService.APPROVAL_API_BASE_URL}/approvals`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify(requestBody),
        }
      );

      if (!response.ok) {
        throw new ApiError(response.status, response.statusText);
      }

      return await response.json();
    } catch (error) {
      console.error('Error in createApproval: ', error);
      throw error;
    }
  }

  async retrieveApproval(
    approvalId: string,
    userId: string,
    accessToken: string
  ): Promise<RetrieveApprovalResponse> {
    try {
      const response = await fetch(
        `${ApprovalService.APPROVAL_API_BASE_URL}/approvals/${approvalId}?user=${userId}`,
        {
          method: 'GET',
          headers: {            
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new ApiError(response.status, response.statusText);
      }

      return await response.json();
    } catch (error) {
      console.error('Error in getApproval: ', error);
      throw error;
    }
  }

  async checkApprovalApprovalStatus(
    approvalId: string,
    userId: string,
    accessToken: string
  ): Promise<ApprovalStatus> {
    const startTime = Date.now();

    while (
      Date.now() - startTime <
      ApprovalService.checkApprovalApprovalStatusTimeout
    ) {
      const approval = await this.retrieveApproval(approvalId, userId, accessToken);
      
      if (approval.status === 'confirmed' || approval.status === 'denied') {
        return approval.status;
      }

      await new Promise((resolve) =>
        setTimeout(resolve, ApprovalService.checkApprovalApprovalStatusInterval)
      );
    }

    return 'denied';
  }
}

export default ApprovalService;
